import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Edit,
  Plus,
  Power,
  Tag,
  Wrench,
  Mail,
  Phone,
  Globe,
  MapPin,
  User,
  FileSpreadsheet,
  TrendingUp,
  Calendar,
  FileText,
  Activity as ActivityIcon,
  Truck,
} from "lucide-react";
import DetailPageLayout from "@/components/layout/DetailPageLayout";
import DetailPageHeader from "@/components/layout/DetailPageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/useAuth";
import { useNotify } from "@/hooks/useNotify";
import { formatCurrency } from "@/lib/utils";
import { formatPaymentTerms } from "@/components/suppliers/paymentTerms";
import SupplierNotes from "@/components/suppliers/SupplierNotes";
import SupplierActivity from "@/components/suppliers/SupplierActivity";
import SupplierDocuments, { useSupplierDocumentSummary } from "@/components/suppliers/documents/SupplierDocuments";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("") || "?";

const SupplierDetailsPage: React.FC = () => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const companyId = profile?.company_id;

  const supplierQuery = useQuery({
    queryKey: ["supplier-details", supplierId, companyId],
    enabled: !!supplierId && !!companyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("*")
        .eq("id", supplierId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const supplier = supplierQuery.data;

  const categoryQuery = useQuery({
    queryKey: ["supplier-category", supplier?.category_id],
    enabled: !!supplier?.category_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("supplier_categories")
        .select("id, name")
        .eq("id", supplier!.category_id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const tradesQuery = useQuery({
    queryKey: ["supplier-trades", supplier?.trade_ids],
    enabled: !!supplier?.trade_ids && supplier.trade_ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("supplier_trades")
        .select("id, name")
        .in("id", supplier!.trade_ids as string[]);
      if (error) throw error;
      return data ?? [];
    },
  });

  const lineItemsQuery = useQuery({
    queryKey: ['supplier-detail-line-items', supplierId],
    enabled: !!supplierId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('internal_line_item_price_sources')
        .select(`
          last_seen_price,
          last_checked_at,
          internal_line_items!internal_line_item_id(
            id, title, description, base_unit, image_urls, is_active, company_id
          )
        `)
        .eq('supplier_id', supplierId!)
        .eq('internal_line_items.is_active', true)
        .order('last_checked_at', { ascending: false });
      if (error) throw error;
      const seen = new Set<string>();
      return ((data ?? []) as any[])
        .filter((row) => {
          const item = row.internal_line_items;
          if (!item || seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        })
        .map((row) => ({
          ...row.internal_line_items,
          last_seen_price: row.last_seen_price,
          last_checked_at: row.last_checked_at,
        }));
    },
  });

  const handleToggleActive = async () => {
    if (!supplier) return;
    const nextActive = !supplier.is_active;
    const { error, data } = await supabase
      .from("suppliers")
      .update({ is_active: nextActive })
      .eq("id", supplier.id)
      .select()
      .maybeSingle();
    if (error || !data) {
      notify.error(error ?? new Error("Failed to update supplier"));
      return;
    }
    notify.success(`Supplier ${nextActive ? "activated" : "deactivated"}`);
    queryClient.invalidateQueries({ queryKey: ["supplier-details", supplierId] });
  };

  if (supplierQuery.isLoading) {
    return (
      <DetailPageLayout>
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40 w-full" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </DetailPageLayout>
    );
  }

  if (!supplier) {
    return (
      <DetailPageLayout>
        <div className="text-center py-12">
          <p className="text-lg text-muted-foreground">Supplier not found.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate("/cost-center/suppliers")}
          >
            Back to Suppliers
          </Button>
        </div>
      </DetailPageLayout>
    );
  }

  const addressParts = [
    supplier.street_address,
    supplier.suburb,
    supplier.city,
    supplier.province_state,
    supplier.postal_code,
  ].filter(Boolean);

  const lineItemsCount = lineItemsQuery.data?.length ?? 0;
  const previewItems = lineItemsQuery.data ?? [];
  const lastPriceUpdate = previewItems.reduce<string | null>((acc, item) => {
    if (!item.last_checked_at) return acc;
    if (!acc || new Date(item.last_checked_at) > new Date(acc)) return item.last_checked_at;
    return acc;
  }, null);

  return (
    <DetailPageLayout>
      <DetailPageHeader
        title={supplier.name}
        status={{
          label: supplier.is_active ? "Active" : "Inactive",
          variant: supplier.is_active ? "success" : "secondary",
        }}
        backLink={{ label: "Back to Suppliers", href: "/cost-center/suppliers" }}
        secondaryActions={[
          {
            label: "Edit",
            icon: Edit,
            onClick: () => navigate(`/cost-center/suppliers/edit/${supplier.id}`),
          },
          {
            label: "Add Line Item",
            icon: Plus,
            onClick: () =>
              navigate(
                `/cost-center/suppliers/${supplier.id}/line-items/create`,
              ),
          },
          {
            label: "View All Line Items",
            icon: FileSpreadsheet,
            onClick: () =>
              navigate(`/cost-center/suppliers/${supplier.id}/line-items`),
          },
          {
            label: supplier.is_active ? "Deactivate" : "Activate",
            icon: Power,
            onClick: handleToggleActive,
            requiresConfirmation: {
              title: supplier.is_active ? "Deactivate supplier" : "Activate supplier",
              description: supplier.is_active
                ? `"${supplier.name}" will be marked inactive. You can re-activate them later.`
                : `"${supplier.name}" will be marked active again.`,
            },
          },
        ]}
      />

      {/* Header / overview card */}
      <Card>
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-muted text-primary text-xl font-semibold overflow-hidden ring-1 ring-border">
              {supplier.logo_url && supplier.logo_url.trim() ? (
                <img src={supplier.logo_url} alt={`${supplier.name} logo`} className="h-full w-full object-contain" />
              ) : (
                initialsOf(supplier.name)
              )}
            </div>
            <div className="flex-1 grid gap-x-8 gap-y-6 sm:grid-cols-2">
              {/* Contact */}

              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">Contact</h3>
                {supplier.contact_name ? (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{supplier.contact_name}</span>
                  </div>
                ) : null}
                {supplier.email ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a className="hover:underline" href={`mailto:${supplier.email}`}>
                      {supplier.email}
                    </a>
                  </div>
                ) : null}
                {supplier.phone ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a className="hover:underline" href={`tel:${supplier.phone}`}>
                      {supplier.phone}
                    </a>
                  </div>
                ) : null}
                {supplier.website ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <a
                      className="hover:underline truncate"
                      href={supplier.website.startsWith("http") ? supplier.website : `https://${supplier.website}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {supplier.website}
                    </a>
                  </div>
                ) : null}
                {!supplier.contact_name && !supplier.email && !supplier.phone && !supplier.website ? (
                  <p className="text-sm text-muted-foreground">No contact details.</p>
                ) : null}
              </div>

              {/* Address + Terms */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">Address</h3>
                  {addressParts.length > 0 ? (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <span>{addressParts.join(", ")}</span>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No address on file.</p>
                  )}
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-medium text-muted-foreground">Payment terms</h3>
                  <p className="text-sm">
                    {formatPaymentTerms(supplier.payment_terms_preset, supplier.payment_terms_custom)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={FileSpreadsheet}
          label="Line items"
          value={lineItemsQuery.isLoading ? "—" : String(lineItemsCount)}
        />
        <StatCard
          icon={TrendingUp}
          label="Total spend (YTD)"
          value="—"
          hint="Not tracked yet"
        />
        <StatCard
          icon={Calendar}
          label="Last price update"
          value={
            lineItemsQuery.isLoading
              ? "—"
              : lastPriceUpdate
                ? format(new Date(lastPriceUpdate), "dd MMMM yyyy")
                : "—"
          }
        />
        <StatCard
          icon={FileText}
          label="Active estimates"
          value="—"
          hint="Not tracked yet"
        />
      </div>

      {/* Classification & Trades */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Classification & Trades</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="text-xs font-medium text-muted-foreground uppercase mb-2">Classification</h4>
            {categoryQuery.data ? (
              <Badge variant="outline" className="w-fit">
                {categoryQuery.data.name}
              </Badge>
            ) : (
              <p className="text-sm text-muted-foreground">No category assigned.</p>
            )}
          </div>
          <div>
            <h4 className="text-xs font-medium text-muted-foreground uppercase mb-2">Trades</h4>
            {tradesQuery.data && tradesQuery.data.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {tradesQuery.data.map((trade) => (
                  <Badge key={trade.id} variant="outline" className="flex items-center gap-1">
                    <Wrench className="h-3 w-3" />
                    {trade.name}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No trades assigned.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Line items preview */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Recent line items</CardTitle>
          {lineItemsCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                navigate(`/cost-center/suppliers/${supplier.id}/line-items`)
              }
            >
              View all ({lineItemsCount})
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          {lineItemsQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : previewItems.length === 0 ? (
            <div className="py-8 text-center space-y-3">
              <p className="text-sm text-muted-foreground">No line items yet for this supplier.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  navigate(`/cost-center/suppliers/${supplier.id}/line-items/create`)
                }
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Line Item
              </Button>
            </div>
          ) : (
            <ul className="divide-y">
              {previewItems.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{item.title}</p>
                    {item.base_unit ? (
                      <p className="text-xs text-muted-foreground">per {item.base_unit}</p>
                    ) : null}
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-sm tabular-nums whitespace-nowrap">
                      {formatCurrency(Number(item.last_seen_price ?? 0))}
                    </span>
                    {item.last_checked_at ? (
                      <p className="text-xs text-muted-foreground">
                        Last seen {format(new Date(item.last_checked_at), "dd MMMM yyyy")}
                      </p>
                    ) : null}

                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Notes / Documents / Activity */}
      <SupplierTabs supplierId={supplier.id} />
    </DetailPageLayout>
  );
};

const SupplierTabs: React.FC<{ supplierId: string }> = ({ supplierId }) => {
  const summary = useSupplierDocumentSummary(supplierId);
  const docSubLabel =
    summary.total === 0
      ? ""
      : summary.expired > 0
      ? ` · ${summary.expired} expired`
      : summary.expiring > 0
      ? ` · ${summary.expiring} expiring`
      : "";

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <Tabs defaultValue="notes" className="w-full">
          <TabsList className="grid w-full grid-cols-3 sm:w-auto sm:inline-grid">
            <TabsTrigger value="notes" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Notes
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Documents
              {summary.total > 0 && (
                <Badge variant="outline" className="ml-1 px-1.5 py-0 text-[10px]">
                  {summary.total}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-2">
              <ActivityIcon className="h-4 w-4" />
              Activity
            </TabsTrigger>
          </TabsList>
          <TabsContent value="notes" className="mt-4">
            <SupplierNotes supplierId={supplierId} />
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            {summary.total > 0 && (
              <p className="text-xs text-muted-foreground mb-3">
                {summary.total} document{summary.total === 1 ? "" : "s"}
                {docSubLabel}
              </p>
            )}
            <SupplierDocuments supplierId={supplierId} />
          </TabsContent>
          <TabsContent value="activity" className="mt-4">
            <SupplierActivity supplierId={supplierId} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint?: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, hint }) => (
  <Card>
    <CardContent className="p-5 sm:p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold font-montserrat truncate">{value}</div>
      {hint ? <p className="text-xs text-muted-foreground mt-1 italic">{hint}</p> : null}
    </CardContent>
  </Card>
);

export default SupplierDetailsPage;
